import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Settings, Check } from 'lucide-react';
import { 
  getCookieConsent, 
  saveCookieConsent, 
  hasUserMadeCookieChoice,
  initializeTracking,
  DEFAULT_COOKIE_SETTINGS 
} from '../utils/cookieUtils';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState(DEFAULT_COOKIE_SETTINGS);

  useEffect(() => {
    // Check if user has already made a choice
    if (!hasUserMadeCookieChoice()) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    saveCookieConsent(consent);
    setIsVisible(false);
    
    // Initialize tracking
    initializeTracking();
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true, // Always required
      analytics: false,
      marketing: false
    };
    saveCookieConsent(consent);
    setIsVisible(false);
  };

  const handleSaveSettings = () => {
    saveCookieConsent(cookieSettings);
    setIsVisible(false);
    setShowSettings(false);
    
    // Initialize tracking based on settings
    initializeTracking();
  };

  const handleToggleSetting = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    
    setCookieSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-black border-t-2 border-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!showSettings ? (
          // Main Cookie Banner
          <div className="py-4 md:py-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg mb-2">
                  {t('cookies.title')}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {t('cookies.description')}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center justify-center px-4 py-2 bg-transparent border border-white text-white hover:bg-white hover:text-black transition-colors duration-200 text-sm font-medium"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {t('cookies.customize')}
                </button>
                
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 bg-transparent border border-gray-400 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-200 text-sm font-medium"
                >
                  {t('cookies.rejectAll')}
                </button>
                
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 bg-white text-black hover:bg-gray-200 transition-colors duration-200 text-sm font-medium font-bold"
                >
                  {t('cookies.acceptAll')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Cookie Settings Panel
          <div className="py-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-xl">
                {t('cookies.settingsTitle')}
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700">
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">
                    {t('cookies.necessary.title')}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    {t('cookies.necessary.description')}
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-white rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-black rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 mt-1 block text-center">
                    {t('cookies.required')}
                  </span>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700">
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">
                    {t('cookies.analytics.title')}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    {t('cookies.analytics.description')}
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => handleToggleSetting('analytics')}
                    className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
                      cookieSettings.analytics 
                        ? 'bg-white justify-end' 
                        : 'bg-gray-600 justify-start'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full transition-colors duration-200 mx-1 ${
                      cookieSettings.analytics ? 'bg-black' : 'bg-gray-400'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700">
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">
                    {t('cookies.marketing.title')}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    {t('cookies.marketing.description')}
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => handleToggleSetting('marketing')}
                    className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
                      cookieSettings.marketing 
                        ? 'bg-white justify-end' 
                        : 'bg-gray-600 justify-start'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full transition-colors duration-200 mx-1 ${
                      cookieSettings.marketing ? 'bg-black' : 'bg-gray-400'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-transparent border border-gray-400 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                {t('cookies.cancel')}
              </button>
              <button
                onClick={handleSaveSettings}
                className="px-4 py-2 bg-white text-black hover:bg-gray-200 transition-colors duration-200 text-sm font-medium font-bold"
              >
                {t('cookies.saveSettings')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;